import { useRef } from "react";
import "./CodeInput.scss";

/**
 * 6 位验证码输入：自动跳到下一格，退格回上一格，支持整段粘贴。
 * value 为当前已输入的字符串（长度 0~6），onChange 回传最新字符串。
 */
const LENGTH = 6;

const CodeInput: React.FC<{
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const chars = value.split("").slice(0, LENGTH);

  const focusAt = (i: number) => {
    const el = inputs.current[i];
    if (el) el.focus();
  };

  const handleChange = (i: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = value.split("");
    next[i] = digit;
    const joined = next.join("").slice(0, LENGTH);
    onChange(joined);
    if (digit && i < LENGTH - 1) focusAt(i + 1);
  };

  const handleKeyDown = (
    i: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !chars[i] && i > 0) {
      focusAt(i - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, LENGTH);
    if (pasted) {
      onChange(pasted);
      focusAt(Math.min(pasted.length, LENGTH - 1));
    }
  };

  return (
    <div className="cs-code-input">
      {Array.from({ length: LENGTH }).map((_, i) => {
        const filled = !!chars[i];
        return (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            disabled={disabled}
            value={chars[i] || ""}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={`cs-code-input__box${
              filled ? " cs-code-input__box--filled" : ""
            }`}
          />
        );
      })}
    </div>
  );
};

export default CodeInput;
